import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Button, Table, Modal, Form, Input, InputNumber,
  Select, Tag, Space, Spin, Progress, Statistic, Switch, Alert
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  PlusOutlined, DeleteOutlined, ExclamationCircleOutlined,
  BankOutlined, ArrowUpOutlined, ArrowDownOutlined,
  EditOutlined, HistoryOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import dayjs from 'dayjs';
import { apiService } from '../services/api';
import type { ReserveFund as ReserveFundData, ReserveFundTransaction, ReserveFundTransactionType } from '../types';

const { Option } = Select;

const TX_TYPE_CONFIG: Record<ReserveFundTransactionType, { color: string; label: string; sign: 1 | -1 }> = {
  CONTRIBUTION: { color: 'green',  label: 'Contribution',  sign:  1 },
  INTEREST:     { color: 'cyan',   label: 'Interest',      sign:  1 },
  WITHDRAWAL:   { color: 'red',    label: 'Withdrawal',    sign: -1 },
  ADJUSTMENT:   { color: 'orange', label: 'Adjustment',    sign:  1 },
};

export const ReserveFund: React.FC = () => {
  const { currentCondo } = useAuthStore();
  const { user } = useAuth();
  const canManage = user?.role === 'super_admin_condo' || user?.role === 'admin';

  const [funds, setFunds] = useState<ReserveFundData[]>([]);
  const [transactions, setTransactions] = useState<Record<string, ReserveFundTransaction[]>>({});
  const [selectedFundId, setSelectedFundId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [txLoading, setTxLoading] = useState(false);

  // Modals
  const [fundModalOpen, setFundModalOpen] = useState(false);
  const [editingFund, setEditingFund] = useState<ReserveFundData | null>(null);
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [txFundId, setTxFundId] = useState<string | null>(null);
  const [fundForm] = Form.useForm();
  const [txForm] = Form.useForm();

  const condoId = currentCondo?.id;

  useEffect(() => {
    if (!condoId) return;
    loadFunds();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; loadFunds is a stable zustand action
  }, [condoId]);

  const loadFunds = async () => {
    if (!condoId) return;
    setLoading(true);
    try {
      const data = await apiService.getReserveFunds(condoId);
      setFunds(data);
      if (data.length > 0 && !selectedFundId) {
        setSelectedFundId(data[0].id!);
        loadTransactions(data[0].id!);
      } else if (selectedFundId) {
        loadTransactions(selectedFundId);
      }
    } catch {
      staticApi.message.error('Failed to load reserve funds');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (fundId: string) => {
    setTxLoading(true);
    try {
      const data = await apiService.getReserveFundTransactions(fundId);
      setTransactions(prev => ({ ...prev, [fundId]: data }));
    } catch {
      staticApi.message.error('Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  };

  const handleSelectFund = (id: string) => {
    setSelectedFundId(id);
    if (!transactions[id]) loadTransactions(id);
  };

  const handleSaveFund = async (values: any) => {
    if (!condoId) return;
    try {
      if (editingFund?.id) {
        await apiService.updateReserveFund(editingFund.id, { ...values, condoId });
        staticApi.message.success('Fund updated');
      } else {
        await apiService.createReserveFund({ ...values, condoId, currentBalance: 0, isActive: true });
        staticApi.message.success('Fund created');
      }
      setFundModalOpen(false);
      setEditingFund(null);
      fundForm.resetFields();
      loadFunds();
    } catch {
      staticApi.message.error('Failed to save fund');
    }
  };

  const handleDeleteFund = (fund: ReserveFundData) => {
    staticApi.modal.confirm({
      title: `Delete "${fund.name}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'All transaction history will be lost.',
      okType: 'danger',
      async onOk() {
        await apiService.deleteReserveFund(fund.id!);
        staticApi.message.success('Fund deleted');
        if (selectedFundId === fund.id) setSelectedFundId(null);
        loadFunds();
      },
    });
  };

  const handleAddTransaction = async (values: any) => {
    if (!txFundId) return;
    const fund = funds.find(f => f.id === txFundId);
    if (!fund) return;
    try {
      await apiService.addReserveFundTransaction(txFundId, {
        reserveFundId: txFundId,
        condoId: condoId!,
        type: values.type,
        amount: values.amount,
        description: values.description,
        // Sprint 145 (R14-9) — a reserve-fund transaction recorded after 20:00
        // EDT was dated the following day, i.e. into the next reporting period.
        date: values.date || dayjs().format('YYYY-MM-DD'),
        createdBy: user?.name || '',
      });
      staticApi.message.success('Transaction recorded');
      setTxModalOpen(false);
      txForm.resetFields();
      loadFunds();
      loadTransactions(txFundId);
    } catch {
      staticApi.message.error('Failed to record transaction');
    }
  };

  const handleDeleteTx = (tx: ReserveFundTransaction) => {
    staticApi.modal.confirm({
      title: 'Delete this transaction?',
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      async onOk() {
        await apiService.deleteReserveFundTransaction(tx.id!);
        staticApi.message.success('Transaction deleted');
        loadFunds();
        if (tx.reserveFundId) loadTransactions(tx.reserveFundId);
      },
    });
  };

  const selectedFund = funds.find(f => f.id === selectedFundId);
  const selectedTx = selectedFundId ? (transactions[selectedFundId] || []) : [];

  const healthColor = (pct: number) => pct >= 80 ? '#52c41a' : pct >= 50 ? '#faad14' : '#f5222d';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Reserve Fund Management</h1>
          <p style={{ color: '#666', margin: 0 }}>Track capital reserves, contributions, and withdrawals</p>
        </div>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setEditingFund(null);
            fundForm.resetFields();
            setFundModalOpen(true);
          }}>
            New Fund
          </Button>
        )}
      </div>

      {funds.length === 0 && !loading && (
        <Alert
          type="info"
          showIcon
          message="No reserve funds found. Create your first fund to start tracking capital reserves."
          style={{ marginBottom: 24 }}
        />
      )}

      <Spin spinning={loading}>
        {/* Fund Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {funds.map(fund => {
            const pct = Number(fund.percentFunded ?? 0);
            const isSelected = selectedFundId === fund.id;
            return (
              <Col xs={24} sm={12} lg={8} key={fund.id}>
                <Card
                  hoverable
                  onClick={() => handleSelectFund(fund.id!)}
                  style={{
                    borderRadius: 12,
                    border: isSelected ? '2px solid #6366f1' : '1px solid #e8e8e8',
                    cursor: 'pointer',
                  }}
                  bodyStyle={{ padding: 20 }}
                  extra={canManage && (
                    <Space onClick={e => e.stopPropagation()}>
                      <Button size="small" icon={<EditOutlined />} onClick={() => {
                        setEditingFund(fund);
                        fundForm.setFieldsValue(fund);
                        setFundModalOpen(true);
                      }} />
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteFund(fund)} />
                    </Space>
                  )}
                  title={<Space><BankOutlined style={{ color: '#6366f1' }} />{fund.name}</Space>}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Current Balance"
                        value={Number(fund.currentBalance)}
                        prefix="$"
                        precision={2}
                        valueStyle={{ color: '#6366f1', fontSize: 18 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Target"
                        value={Number(fund.targetBalance)}
                        prefix="$"
                        precision={2}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 4 }}>
                      <span>Funded</span>
                      <span style={{ fontWeight: 600, color: healthColor(pct) }}>{pct.toFixed(1)}%</span>
                    </div>
                    <Progress
                      percent={Math.min(pct, 100)}
                      strokeColor={healthColor(pct)}
                      showInfo={false}
                      size="small"
                    />
                  </div>
                  {!fund.isActive && <Tag color="default" style={{ marginTop: 8 }}>Inactive</Tag>}
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Transaction History for selected fund */}
        {selectedFund && (
          <Card
            title={
              <Space>
                <HistoryOutlined />
                {selectedFund.name} — Transaction History
              </Space>
            }
            extra={canManage && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setTxFundId(selectedFund.id!);
                  txForm.resetFields();
                  setTxModalOpen(true);
                }}
              >
                Record Transaction
              </Button>
            )}
          >
            <Spin spinning={txLoading}>
              <Table
                rowKey="id"
                dataSource={selectedTx}
                pagination={{ pageSize: 15 }}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    sorter: (a, b) => a.date.localeCompare(b.date),
                    defaultSortOrder: 'descend',
                  },
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type',
                    render: (t: ReserveFundTransactionType) => (
                      <Tag color={TX_TYPE_CONFIG[t]?.color}>{TX_TYPE_CONFIG[t]?.label ?? t}</Tag>
                    ),
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    key: 'description',
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amt: number, record: ReserveFundTransaction) => {
                      const sign = TX_TYPE_CONFIG[record.type]?.sign ?? 1;
                      const color = sign > 0 ? '#52c41a' : '#f5222d';
                      const prefix = sign > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;
                      return <span style={{ color, fontWeight: 600 }}>{prefix} ${Number(amt).toFixed(2)}</span>;
                    },
                    align: 'right' as const,
                  },
                  {
                    title: 'Recorded By',
                    dataIndex: 'createdBy',
                    key: 'createdBy',
                    render: (v: string) => v || '—',
                  },
                  ...(canManage ? [{
                    title: '',
                    key: 'actions',
                    render: (_: any, record: ReserveFundTransaction) => (
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteTx(record)} />
                    ),
                  }] : []),
                ]}
              />
            </Spin>
          </Card>
        )}
      </Spin>

      {/* Fund Create/Edit Modal */}
      <Modal
        title={editingFund ? 'Edit Reserve Fund' : 'New Reserve Fund'}
        open={fundModalOpen}
        onCancel={() => { setFundModalOpen(false); setEditingFund(null); fundForm.resetFields(); }}
        footer={null}
      >
        <Form form={fundForm} layout="vertical" onFinish={handleSaveFund}>
          <Form.Item name="name" label="Fund Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Capital Reserve Fund" />
          </Form.Item>
          <Form.Item name="targetBalance" label="Target Balance ($)" rules={[{ required: true }]}>
            <InputNumber min={0} step={1000} prefix="$" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setFundModalOpen(false); setEditingFund(null); fundForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit">{editingFund ? 'Update' : 'Create'}</Button>
          </div>
        </Form>
      </Modal>

      {/* Transaction Modal */}
      <Modal
        title="Record Transaction"
        open={txModalOpen}
        onCancel={() => { setTxModalOpen(false); txForm.resetFields(); }}
        footer={null}
      >
        <Form form={txForm} layout="vertical" onFinish={handleAddTransaction}>
          <Form.Item name="type" label="Transaction Type" rules={[{ required: true }]}>
            <Select>
              <Option value="CONTRIBUTION">Contribution (money in)</Option>
              <Option value="INTEREST">Interest Earned</Option>
              <Option value="WITHDRAWAL">Withdrawal (money out)</Option>
              <Option value="ADJUSTMENT">Adjustment / Correction</Option>
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Amount ($)" rules={[{ required: true }]}>
            <InputNumber min={0.01} step={0.01} prefix="$" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input placeholder="Brief description of this transaction" />
          </Form.Item>
          <Form.Item name="date" label="Date">
            {/* Sprint 145 (R14-9) — the field pre-filled tomorrow's date after
                20:00 EDT, so the default silently disagreed with the operator. */}
            <Input type="date" defaultValue={dayjs().format('YYYY-MM-DD')} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setTxModalOpen(false); txForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit">Record</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
